import React, { useEffect, useMemo, useRef } from "react";

// Components
import EmptyChart from "../../../../../../../../components/EmptyChart";
import Spinner from "../../../../../../../../components/Spinner";

import { ChartOptions } from "../../../../../../schemas/report";

// hooks
import useStore from "../../../../store";
// utils
import { ChartType as PYChart } from "../../../../store/enums";

import { useGetGridDataCount, useGetRawReviewReportData } from "../../../../../../api/hooks";

import { getPointTypeFromDataseries } from "../ChartHooks/ScatterChart/utils";

import Config from "../../../../Config";
import BarChart from "../ChartHooks/BarChart/BarChart";
import useChartOptions from "../ChartHooks/UseChartOptions/useChartOptions";
import { useDownloadImage } from "../ChartHooks/UseDownloadImage/useDownloadImage";
import usePreloadImages from "../ChartHooks/preloadImages";
import { ChartContainer, VisualizationHeader } from "../Layout";
import css from "../Layout/Layout.module.scss";
import OptionSettings from "../shared/OptionSettings";
import { rawReviewReportAvatarsExtractor } from "../shared/avatarExtractor";

const { yAxisRangeOptions } = OptionSettings[PYChart.BarChart];

const ReportingBarChart = () => {
	const chartRef = useRef(null);
	const [getAppliedFilters, report, canViewSummary] = useStore((state) => [
		state.getAppliedFilters,
		state.report,
		state.canViewSummary,
	]);

	const {
		summarizeBySetting,
		visualizeOption,
		aggregationOperations,
		sortSettings,
		anyAxisOptions,
		dataSeriesOptions,
		previousChart,
		previousVisualizeOption,
		getChartDataFromRawData,
		setVisualizeOptions,
		getAxisOptionsAsColumns,
		selectableSortSettings,
	} = useChartOptions(PYChart.BarChart);

	useEffect(() => {
		handleSetDefaultBarChartOptions();
	}, []);

	useEffect(() => {
		if (!anyAxisOptions?.some((option) => option.value === visualizeOption?.xAxis?.column)) {
			if (visualizeOption?.xAxis?.column) {
				handleSetDefaultBarChartOptions(true);
			}
		}
	}, [anyAxisOptions?.length]);

	const { downloadImage } = useDownloadImage(chartRef, {
		chartType: PYChart.BarChart,
		xAxisLabel: "Metric",
	});

	const variables = {
		questionGroupingSessionID: report?.questionGroupingSessionID,
		columns: getAxisOptionsAsColumns(),
		filters: getAppliedFilters(),
		pageNumber: "1",
		pageSize: "25",
		sorts: sortSettings,
		summarizeBy: summarizeBySetting,
		aggregationOperations,
	};

	const prefetchVariables: (typeof variables)[] = [];

	for (let i = 0; i < selectableSortSettings?.length; i++) {
		if (
			JSON.stringify(selectableSortSettings[i]) !== JSON.stringify(sortSettings) &&
			(i === 0 || JSON.stringify(selectableSortSettings[i]) !== JSON.stringify(selectableSortSettings[i - 1]))
		) {
			prefetchVariables.push({
				...variables,
				sorts: selectableSortSettings[i],
			});
		}
	}

	const handleSetDefaultBarChartOptions = (forceUpdate = false) => {
		const options: ChartOptions = {};

		if (dataSeriesOptions?.length > 0) {
			const hasRangeOptionCarryover = yAxisRangeOptions.some(
				(option) => option.value === previousVisualizeOption?.dataSeries?.rangeOption
			);
			options.dataSeries = {
				column:
					previousVisualizeOption?.dataSeries?.column ||
					visualizeOption?.dataSeries?.column ||
					dataSeriesOptions[dataSeriesOptions.length - 1].value,
				rangeOption: hasRangeOptionCarryover
					? previousVisualizeOption?.dataSeries?.rangeOption
					: visualizeOption?.dataSeries?.rangeOption || yAxisRangeOptions[1].value,
			};
		}

		if (anyAxisOptions?.length > 0) {
			options.xAxis = {
				column:
					previousVisualizeOption?.xAxis?.column ||
					previousVisualizeOption?.yAxis?.column ||
					anyAxisOptions[0].value,
			};
			if (forceUpdate) {
				options.xAxis.column = anyAxisOptions[0].value;
			}
		}

		// Current behavior: when there is visualization option on first render, that means it came from saved report
		if ((visualizeOption && Object.keys(visualizeOption).length === 0) || forceUpdate) {
			const newVisualizeOption = { [PYChart.BarChart]: { ...visualizeOption, ...options } };
			if (previousChart !== PYChart.BarChart) {
				newVisualizeOption[previousChart] = null;
			}
			setVisualizeOptions(newVisualizeOption);
			return;
		}

		// Current behavior: when there is visualization option on first render, if the selected dataseries columns doesn't exist, default to the first options
		if (visualizeOption && Object.keys(visualizeOption).length > 0) {
			const hasDataSeriesColumn = dataSeriesOptions.some(
				(option) => option.value === visualizeOption.dataSeries?.column
			);
			const hasXAxisColumn = anyAxisOptions.some((option) => option.value === visualizeOption.xAxis?.column);

			if (!hasDataSeriesColumn) {
				options.dataSeries = {
					column: dataSeriesOptions[dataSeriesOptions.length - 1].value,
					rangeOption: yAxisRangeOptions[1].value,
				};
			}

			if (!hasXAxisColumn) {
				options.xAxis = {
					column: anyAxisOptions[0].value,
				};
			}

			const newVisualizeOptions = { [PYChart.BarChart]: { ...visualizeOption, ...options } };
			setVisualizeOptions(newVisualizeOptions);
		}
	};

	const dataSeries = visualizeOption?.dataSeries?.column.split("_");
	const { data: rawData, isLoading: isRawDataLoading } = useGetRawReviewReportData(
		[variables, ...prefetchVariables],
		canViewSummary
	)[0];
	const type = getPointTypeFromDataseries(dataSeries);
	const nameKeyOne = dataSeries ? Config.fieldGroupLookup[dataSeries[1]] : "";
	const nameKeyTwo = dataSeries?.splice(2).join("_") || "";
	const avatarUrls = useMemo(() => rawReviewReportAvatarsExtractor(rawData, type, nameKeyOne, nameKeyTwo), [rawData]);
	const { data: countData } = useGetGridDataCount(variables);
	const { labels, datasets = [] } = useMemo(
		() => getChartDataFromRawData(rawData),
		[JSON.stringify(rawData), JSON.stringify(visualizeOption)]
	);
	const { imagesLoaded } = usePreloadImages(avatarUrls, isRawDataLoading);

	const emtpyDatasets = datasets.filter((item) => item !== null).length === 0;

	// Check if selected column is count aggregation to render the tooltip accordingly
	const selectedCountAggregation =
		anyAxisOptions &&
		anyAxisOptions
			.find((option) => option.value === visualizeOption?.xAxis?.column)
			?.labelWithAggregation.indexOf("(Count)") !== -1;

	return (
		<ChartContainer
			padding="16px 24px 16px 16px"
			height="calc(100% - 5px)"
			width="calc(100% - 20px)"
			testId="barChart"
		>
			{isRawDataLoading && (
				<div className={css.spinner}>
					<Spinner />
				</div>
			)}
			{!isRawDataLoading && emtpyDatasets && canViewSummary && <EmptyChart type={PYChart.BarChart} />}
			{!isRawDataLoading && !emtpyDatasets && imagesLoaded && (
				<>
					<VisualizationHeader
						viewingCount={datasets.length}
						totalCount={countData?.reviewReportsCount?.count || 0}
						options={[
							{
								label: "Download Image",
								action: downloadImage,
							},
						]}
					/>
					<BarChart
						ref={chartRef}
						labels={labels}
						datasets={datasets}
						horizontal
						xLabelText={
							anyAxisOptions &&
							anyAxisOptions.find((option) => option.value === visualizeOption?.xAxis?.column)
								?.labelWithAggregation
						}
						isCountAggregation={selectedCountAggregation}
					/>
				</>
			)}
		</ChartContainer>
	);
};

export default ReportingBarChart;
